import { USE_WBOIT, USE_OVERLAY_DEPTH_COPY } from '../globals';

/**
 * The depth format, compare function, and clear values are exported as constants intentionally to facilitate the
 * specification of WebGPU layouts without requiring access to the actual depth(-stencil) render target.
 */
export const DepthFormat = 'depth32float'; // 'depth24plus', 'depth16unorm'
export const DepthCompare = 'greater';
export const DepthClearValue = 0.0;

export function invertDepthCompare(depthCompare) {
  switch (depthCompare) {
    case 'less':return 'greater';
    case 'greater':return 'less';
    case 'less-equal':return 'greater-equal';
    case 'greater-equal':return 'less-equal';
    default:
  }
  return depthCompare;
}

/**
 * The default number of ID targets (actual count may change when activating category highlighting).
 */
export const IdTargetCount = 2; // always 2: dbId + modelId targets
export const IdFormat = 'rgba8uint';

/** @type {GPUBlendState} */
export const NormalBlend = {
  color: { operation: 'add', srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha' },
  alpha: { operation: 'add', srcFactor: 'one', dstFactor: 'one-minus-src-alpha' }
};

// Note about maxColorAttachmentBytesPerSample limit in WebGPU:
// - Only guaranteed to be >= 32 Bytes
// - Takes into account storage size, not just format bits, e.g. rgb10a2unorm costs 8 bytes, not just 4!
//   (see https://www.w3.org/TR/webgpu/#plain-color-formats)
//
// Our pipeline needs 36 bytes per sample for 3 ID targets:
// - 8 for color (even for useHdr = false)
// - 8 for normals (rgb10a2unorm)
// - 8 for viewDepth (rgb10a2unorm)
// - 4 for dbId (rgba8uint)
// - 4 for modelId (rgba8uint)
// - 4 for categoryId (rgba8uint)
// Total: 36 bytes.
//
// Most modern devices support 64 bytes per sample, but we need a fallback for older ones:
// - the categoryId is only used for a category highlighting and is disabled if only 32 bytes are supported.

// Request extra bytes for category highlighting if the adapter supports it
export const RequiredBytesPerSample = 36;

export class CommonRenderTargets {

  #renderer;
  /** @type {GPUDevice} */
  #device;

  #colorTarget;
  #depthTarget;
  /** @type {GPUTexture|null} Depth copy for the ID-only pass (transparent ID resolution) */
  #idOnlyDepthTarget = null;
  #normalsTarget;
  #viewDepthTarget;
  #overlayTarget;
  #overlayDepthTarget;

  // MSAA resolve targets (only created when sampleCount > 1)
  /** @type {GPUTexture|null} */
  #colorResolveTarget = null;
  /** @type {GPUTexture|null} */
  #depthResolveTarget = null;
  /** @type {GPUTexture|null} */
  #normalsResolveTarget = null;
  /** @type {GPUTexture|null} */
  #viewDepthResolveTarget = null;
  /** @type {GPUTexture|null} */
  #overlayResolveTarget = null;
  /** @type {GPUTexture[]} */
  #idResolveTargets = [];

  // WBOIT Targets
  /** @type {GPUTexture|null} */
  #accumTarget = null;
  /** @type {GPUTextureView|null} */
  #accumView = null;
  /** @type {GPUTexture|null} */
  #accumResolveTarget = null;
  /** @type {GPUTextureView|null} */
  #accumResolveView = null;

  /** @type {GPUTexture|null} */
  #revealageTarget = null;
  /** @type {GPUTextureView|null} */
  #revealageView = null;
  /** @type {GPUTexture|null} */
  #revealageResolveTarget = null;
  /** @type {GPUTextureView|null} */
  #revealageResolveView = null;

  #idTargets;
  #postTargets;

  #colorTargetView;

  #size = [0, 0];

  #presentationFormat;
  #colorTargetFormat;

  /** @type {number} MSAA sample count. 1 = no MSAA, 4 = 4x MSAA. */
  #sampleCount = 1;

  #readBuffer;
  #cachedWholeBuffers;
  #cachedIdBuffersDirty = true;
  #cacheInProgress = false;

  /** @type {GPUColorTargetState[]|null} */
  #targetsList = null;
  /** @type {GPUColorTargetState[]|null} */
  #targetsListEdge = null;
  /** @type {GPUColorTargetState[]|null} */
  #targetsListOverlay = null;

  /** @type {GPUColorTargetState[]|null} */
  #targetsListOIT = null;

  /** @type {boolean} Whether category highlighting is enabled (requires 3rd ID target) */
  #categoryHighlightingEnabled = false;

  /** @import { RenderContextWebGPU } from './RenderContextWebGPU' */

  /**
   * External callback to notify when it's safe to do sync idBuffer reads. Called with true on success,
   * with false on failure (may happen readback is invalidated by a resize).
   * @see RenderContextWebGPU.waitForIdReadback
   * @type {(null | (boolean) => void)}
   */
  onIdReadbackCacheFinished = null;

  constructor(renderer) {
    this.#renderer = renderer;

  }

  /**
   * Returns the number of ID targets based on hardware support and whether category highlighting is enabled.
   * @returns {number} 2 by default, 3 if category highlighting is enabled and hardware supports it.
   */
  getNumIdTargets() {
    return this.#renderer.isCategoryHighlightingSupported() && this.#categoryHighlightingEnabled ? IdTargetCount + 1 : IdTargetCount;
  }

  /**
   * Returns whether category highlighting is currently enabled.
   * @returns {boolean}
   */
  isCategoryHighlightingEnabled() {
    return this.#categoryHighlightingEnabled;
  }

  /**
   * Enables or disables category highlighting. When enabled, a 3rd ID target is allocated
   * (if hardware supports it) to store category IDs for the highlighting effect.
   * @param {boolean} enabled
   */
  setCategoryHighlightingEnabled(enabled) {
    if (this.#categoryHighlightingEnabled === enabled) return;

    const wasEnabled = this.#categoryHighlightingEnabled;
    this.#categoryHighlightingEnabled = enabled;

    if (this.#device && this.#size[0] > 0 && this.#size[1] > 0) {
      this.#updateCategoryIdTarget(wasEnabled, enabled);
    }
    // Invalidate cached target lists so they get rebuilt with the correct number of targets
    this.#targetsList = null;
    this.#targetsListEdge = null;
    this.#targetsListOverlay = null;
    this.#targetsListOIT = null;
  }

  /**
   * Adds or removes the 3rd ID-target (categoryId) based on the enabled state change. It assumes that the id-target-0
   * and id-target-1 are already created and adds/removes the 3rd target accordingly.
   *
   * @param {boolean} wasEnabled - Previous enabled state
   * @param {boolean} isEnabled - New enabled state
   */
  #updateCategoryIdTarget(wasEnabled, isEnabled) {
    if (!this.#renderer.isCategoryHighlightingSupported()) return; // Hardware doesn't support it anyway

    const [w, h] = this.#size;
    const sampleCount = this.#sampleCount;

    if (isEnabled && !wasEnabled) {// Add the 3rd ID-target

      console.assert(this.#idTargets.length === IdTargetCount, 'ID targets length mismatch when adding category ID target.');

      const categoryIdTarget = this.#device.createTexture({
        label: 'id-target-2',
        size: [w, h],
        format: IdFormat,
        sampleCount,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_SRC
      });
      this.#idTargets.push(categoryIdTarget);

      if (sampleCount > 1) {
        const categoryIdResolve = this.#device.createTexture({
          label: 'id-resolve-target-2',
          size: [w, h],
          format: IdFormat,
          usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING |
          GPUTextureUsage.COPY_SRC | GPUTextureUsage.STORAGE_BINDING
        });
        this.#idResolveTargets.push(categoryIdResolve);
      }

    } else if (!isEnabled && wasEnabled) {// Remove and destroy the 3rd ID target

      console.assert(this.#idTargets.length === IdTargetCount + 1, 'ID targets length mismatch when removing category ID target.');

      const categoryIdTarget = this.#idTargets.pop();
      categoryIdTarget?.destroy();

      if (this.#idResolveTargets.length > IdTargetCount) {
        const categoryIdResolve = this.#idResolveTargets.pop();
        categoryIdResolve?.destroy();
      }
    }
  }

  init(useHdrTarget, sampleCount = 1) {
    this.#device = this.#renderer.getDevice();
    this.#presentationFormat = navigator.gpu.getPreferredCanvasFormat();
    if (useHdrTarget) {
      this.#colorTargetFormat = 'rgba16float';
    } else {
      this.#colorTargetFormat = this.#presentationFormat;
    }
    this.#sampleCount = sampleCount;
  }

  /** @returns {number} The current MSAA sample count. */
  getSampleCount() {
    return this.#sampleCount;
  }

  /**
   * Sets the MSAA sample count. Requires a resize to take effect.
   * @param {number} sampleCount - 1 for no MSAA, 4 for 4x MSAA.
   */
  setSampleCount(sampleCount) {
    if (this.#sampleCount !== sampleCount) {
      this.#sampleCount = sampleCount;
      this.#size[0] = 0;
      this.#size[1] = 0;
    }
  }

  cleanup() {
    this.#depthTarget?.destroy();
    this.#colorTarget?.destroy();
    this.#overlayTarget?.destroy();
    this.#overlayDepthTarget?.destroy();
    this.#normalsTarget?.destroy();
    this.#viewDepthTarget?.destroy();
    if (USE_WBOIT) {
      this.#accumTarget?.destroy();
      this.#revealageTarget?.destroy();
      this.#idOnlyDepthTarget?.destroy();
    }
    this.#idTargets?.forEach((t) => t?.destroy());
    this.#postTargets?.forEach((t) => t?.destroy());

    // Destroy MSAA resolve targets
    this.#colorResolveTarget?.destroy();
    this.#depthResolveTarget?.destroy();
    this.#normalsResolveTarget?.destroy();
    this.#viewDepthResolveTarget?.destroy();
    this.#overlayResolveTarget?.destroy();
    this.#accumResolveTarget?.destroy();
    this.#revealageResolveTarget?.destroy();
    this.#idResolveTargets?.forEach((t) => t?.destroy());

    this.#depthTarget = null;
    this.#colorTarget = null;
    this.#overlayTarget = null;
    this.#overlayDepthTarget = null;
    this.#normalsTarget = null;
    if (USE_WBOIT) {
      this.#accumTarget = null;
      this.#accumView = null;
      this.#accumResolveTarget = null;
      this.#accumResolveView = null;
      this.#revealageTarget = null;
      this.#revealageView = null;
      this.#revealageResolveTarget = null;
      this.#revealageResolveView = null;
      this.#idOnlyDepthTarget = null;
    }
    this.#idTargets = [];
    this.#postTargets = [];

    this.#colorResolveTarget = null;
    this.#depthResolveTarget = null;
    this.#normalsResolveTarget = null;
    this.#viewDepthResolveTarget = null;
    this.#overlayResolveTarget = null;
    this.#idResolveTargets = [];
  }

  resize(w, h) {

    // Avoid reallocation if size didn't change
    if (this.#size[0] === w && this.#size[1] === h) {
      return;
    }

    this.#targetsList = null;
    this.#targetsListEdge = null;
    this.#targetsListOverlay = null;
    this.#targetsListOIT = null;

    this.#size[0] = w;
    this.#size[1] = h;

    this.#cachedWholeBuffers = null;
    this.#cachedIdBuffersDirty = true;

    this.cleanup();

    if (w === 0 || h === 0) {
      return;
    }

    const sampleCount = this.#sampleCount;

    // When MSAA is on, render targets are multisampled. Separate single-sampled resolve
    // targets are created for post-processing reads. When MSAA is off (sampleCount=1),
    // the render targets serve both purposes directly.
    // Usage flags are kept as the superset — the few extra flags on MS textures are harmless.

    this.#colorTarget = this.#device.createTexture({
      label: 'color-target',
      size: [w, h],
      format: this.#colorTargetFormat,
      sampleCount,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_SRC
    });

    this.#colorTargetView = this.#colorTarget.createView();

    this.#depthTarget = this.#device.createTexture({
      label: 'depth-target',
      size: [w, h],
      format: DepthFormat,
      sampleCount,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_SRC
    });

    this.#overlayTarget = this.#device.createTexture({
      label: 'overlay-target',
      size: [w, h],
      format: this.#colorTargetFormat,
      sampleCount,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
    });

    if (USE_OVERLAY_DEPTH_COPY) {
      this.#overlayDepthTarget = this.#device.createTexture({
        label: 'overlay-depth-target',
        size: [w, h],
        format: DepthFormat,
        sampleCount,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.COPY_DST
      });
    }

    this.#normalsTarget = this.#device.createTexture({
      label: 'normals-target',
      size: [w, h],
      format: 'rgb10a2unorm',
      sampleCount,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
    });

    this.#viewDepthTarget = this.#device.createTexture({
      label: 'view-depth-target',
      size: [w, h],
      format: 'rgb10a2unorm',
      sampleCount,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
    });

    if (USE_WBOIT) {
      this.#accumTarget = this.#device.createTexture({
        label: 'wboit-accumulation-target',
        size: [w, h],
        format: 'rgba16float',
        sampleCount,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
      });
      this.#accumView = null;

      this.#revealageTarget = this.#device.createTexture({
        label: 'wboit-revealage-target',
        size: [w, h],
        format: 'r8unorm',
        sampleCount,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
      });
      this.#revealageView = null;

      this.#idOnlyDepthTarget = this.#device.createTexture({
        label: 'id-only-depth-target',
        size: [w, h],
        format: DepthFormat,
        sampleCount,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.COPY_DST
      });
    }

    this.#idTargets = new Array(this.getNumIdTargets());
    for (let i = 0; i < this.#idTargets.length; i++) {
      this.#idTargets[i] = this.#device.createTexture({
        label: `id-target-${i}`,
        size: [w, h],
        format: IdFormat,
        sampleCount,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_SRC
      });
    }

    this.#postTargets = new Array(2);
    for (let i = 0; i < this.#postTargets.length; i++) {
      this.#postTargets[i] = this.#device.createTexture({
        label: `post-target-${i}`,
        size: [w, h],
        format: this.#colorTargetFormat,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
      });
    }

    // Create single-sampled resolve targets when MSAA is enabled
    if (sampleCount > 1) {
      this.#colorResolveTarget = this.#device.createTexture({
        label: 'color-resolve-target',
        size: [w, h],
        format: this.#colorTargetFormat,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_SRC
      });

      this.#depthResolveTarget = this.#device.createTexture({
        label: 'depth-resolve-target',
        size: [w, h],
        format: DepthFormat,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_SRC
      });

      this.#normalsResolveTarget = this.#device.createTexture({
        label: 'normals-resolve-target',
        size: [w, h],
        format: 'rgb10a2unorm',
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
      });

      this.#viewDepthResolveTarget = this.#device.createTexture({
        label: 'view-depth-resolve-target',
        size: [w, h],
        format: 'rgb10a2unorm',
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
      });

      this.#overlayResolveTarget = this.#device.createTexture({
        label: 'overlay-resolve-target',
        size: [w, h],
        format: this.#colorTargetFormat,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
      });

      if (USE_WBOIT) {
        this.#accumResolveTarget = this.#device.createTexture({
          label: 'wboit-accumulation-resolve-target',
          size: [w, h],
          format: 'rgba16float',
          usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
        });
        this.#accumResolveView = null;

        this.#revealageResolveTarget = this.#device.createTexture({
          label: 'wboit-revealage-resolve-target',
          size: [w, h],
          format: 'r8unorm',
          usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING
        });
        this.#revealageResolveView = null;
      }

      this.#idResolveTargets = new Array(this.getNumIdTargets());
      for (let i = 0; i < this.#idResolveTargets.length; i++) {
        this.#idResolveTargets[i] = this.#device.createTexture({
          label: `id-resolve-target-${i}`,
          size: [w, h],
          format: IdFormat,
          usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING |
          GPUTextureUsage.COPY_SRC | GPUTextureUsage.STORAGE_BINDING
        });
      }
    }
  }

  getPreferredFormat() {
    return this.#presentationFormat;
  }

  getColorTargetFormat() {
    return this.#colorTargetFormat;
  }

  getColorTarget() {
    return this.#colorTarget;
  }

  getNormalsTarget() {
    return this.#normalsTarget;
  }

  getDepthTarget() {
    return this.#depthTarget;
  }

  getViewDepthTarget() {
    return this.#viewDepthTarget;
  }

  getOverlayTarget() {
    return this.#overlayTarget;
  }

  getOverlayDepthTarget() {
    return this.#overlayDepthTarget;
  }

  /**
   * @returns {GPUTextureView|null}
   */
  getWboitAccumView() {
    console.assert(USE_WBOIT, 'WBOIT is not enabled!');
    if (!this.#accumView) {
      this.#accumView = this.#accumTarget.createView();
    }
    return this.#accumView;
  }

  /** Returns the single-sampled WBOIT accumulation view for texture sampling. */
  getWboitAccumViewForSampling() {
    console.assert(USE_WBOIT, 'WBOIT is not enabled!');
    if (this.#accumResolveTarget) {
      if (!this.#accumResolveView) {
        this.#accumResolveView = this.#accumResolveTarget.createView();
      }
      return this.#accumResolveView;
    }
    return this.getWboitAccumView();
  }

  /**
   * @returns {GPUTextureView|null}
   */
  getWboitRevealageView() {
    console.assert(USE_WBOIT, 'WBOIT is not enabled!');
    if (!this.#revealageView) {
      this.#revealageView = this.#revealageTarget.createView();
    }
    return this.#revealageView;
  }

  /** Returns the single-sampled WBOIT revealage view for texture sampling. */
  getWboitRevealageViewForSampling() {
    console.assert(USE_WBOIT, 'WBOIT is not enabled!');
    if (this.#revealageResolveTarget) {
      if (!this.#revealageResolveView) {
        this.#revealageResolveView = this.#revealageResolveTarget.createView();
      }
      return this.#revealageResolveView;
    }
    return this.getWboitRevealageView();
  }

  /**
   * @returns {GPUColorTargetState[]}
   */
  getWboitTargets() {
    console.assert(USE_WBOIT, 'WBOIT is not enabled!');

    if (this.#targetsListOIT) return this.#targetsListOIT;

    this.#targetsListOIT = [
    {
      format: this.#accumTarget.format,
      blend: {
        color: { operation: 'add', srcFactor: 'one', dstFactor: 'one' },
        alpha: { operation: 'add', srcFactor: 'one', dstFactor: 'one' }
      }
    },
    {
      format: this.#revealageTarget.format,
      blend: {
        color: { operation: 'add', srcFactor: 'zero', dstFactor: 'one-minus-src' },
        alpha: { operation: 'add', srcFactor: 'zero', dstFactor: 'one-minus-src' }
      }
    }];


    return this.#targetsListOIT;
  }

  /**
   * @returns {GPUTextureFormat[]}
   */
  getWboitFormats() {
    console.assert(USE_WBOIT, 'WBOIT is not enabled!');
    return [this.#accumTarget.format, this.#revealageTarget.format];
  }

  getIdTarget(index) {
    return this.#idTargets[index];
  }

  getIdTargets() {
    return this.#idTargets;
  }

  getIdOnlyDepthTarget() {
    return this.#idOnlyDepthTarget;
  }

  getPostTarget(index) {
    return this.#postTargets[index];
  }

  /**
   * Returns views from resolved (single-sampled) targets for post-processing texture sampling.
   * When MSAA is off, returns views from the render targets directly.
   */
  getTargetViewsForBlend() {
    const colorSrc = this.#colorResolveTarget || this.#colorTarget;
    const overlaySrc = this.#overlayResolveTarget || this.#overlayTarget;

    const views = new Array(this.getNumIdTargets() + 2);
    views[0] = colorSrc.createView();
    views[1] = overlaySrc.createView();

    for (let i = 0; i < this.getNumIdTargets(); i++) {
      const idSrc = this.#idResolveTargets?.[i] || this.#idTargets[i];
      views[i + 2] = idSrc.createView();
    }
    return views;
  }

  getColorTargetView() {
    return this.#colorTargetView;
  }

  getTargetSize() {
    return this.#size;
  }

  // --- Sampling getters: return the resolved SS texture for post-processing reads ---

  /** Returns the single-sampled color target for texture sampling (resolve target if MSAA, render target otherwise). */
  getColorTargetForSampling() {
    return this.#colorResolveTarget || this.#colorTarget;
  }

  /** Returns the single-sampled depth target for texture sampling. */
  getDepthTargetForSampling() {
    return this.#depthResolveTarget || this.#depthTarget;
  }

  /** Returns the single-sampled normals target for texture sampling. */
  getNormalsTargetForSampling() {
    return this.#normalsResolveTarget || this.#normalsTarget;
  }

  /** Returns the single-sampled view-depth target for texture sampling. */
  getViewDepthTargetForSampling() {
    return this.#viewDepthResolveTarget || this.#viewDepthTarget;
  }

  /** Returns the single-sampled overlay target for texture sampling. */
  getOverlayTargetForSampling() {
    return this.#overlayResolveTarget || this.#overlayTarget;
  }

  /**
   * Returns the single-sampled ID resolve target for readback/sampling.
   * @param {number} index
   * @returns {GPUTexture}
   */
  getIdTargetForSampling(index) {
    return this.#idResolveTargets?.[index] || this.#idTargets[index];
  }

  /**
   * @returns {GPUColorTargetState[]}
   */
  getTargetsListMainPass() {
    if (this.#targetsList) return this.#targetsList;

    this.#targetsList = [
    { format: this.#colorTarget.format, blend: NormalBlend },
    { format: this.#normalsTarget.format, blend: NormalBlend },
    { format: this.#viewDepthTarget.format, blend: NormalBlend }];


    for (let i = 0; i < this.getNumIdTargets(); i++) {
      this.#targetsList.push({ format: this.#idTargets[i].format });
    }
    return this.#targetsList;
  }

  /**
   * @returns {GPUColorTargetState[]}
   */
  getTargetsListEdgePass() {
    if (this.#targetsListEdge) return this.#targetsListEdge;

    this.#targetsListEdge = [
    { format: this.#colorTarget.format, blend: NormalBlend },
    // Include these targets to be compatible with UberPipeline render-pass, writing being not required
    { format: this.#normalsTarget.format, writeMask: 0 },
    { format: this.#viewDepthTarget.format, writeMask: 0 }];


    for (let i = 0; i < this.getNumIdTargets(); i++) {
      this.#targetsListEdge.push({ format: this.#idTargets[i].format, writeMask: 0 });
    }
    return this.#targetsListEdge;
  }

  /**
   * @returns {GPUColorTargetState[]}
   */
  getOverlayTargetsList() {
    if (this.#targetsListOverlay) return this.#targetsListOverlay;

    this.#targetsListOverlay = [
    { format: this.#colorTarget.format, blend: NormalBlend },
    // Include these targets to be compatible with UberPipeline render-pass, writing being not required
    { format: this.#normalsTarget.format, writeMask: 0 },
    { format: this.#viewDepthTarget.format, writeMask: 0 }];


    for (let i = 0; i < this.getNumIdTargets(); i++) {
      this.#targetsListOverlay.push({ format: this.#idTargets[i].format, writeMask: 0 });
    }
    return this.#targetsListOverlay;
  }


  //Async reading from render targets. This is a better approach than the simulated
  //synchronous reading in readIdTargetPixelsSyncOrFail, and we need to transition
  //APIs like idAtPixel() to use this approach in the long term. This will require
  //adjusting tools that use mouse rollover to use Promises rather than expecting
  //the result directly.
  //
  //Resolves false when another read still holds the shared staging buffer -- bufs are left untouched
  //in that case, so callers must not treat their contents as a result.
  async readIdTargetPixelsAsync(x, y, width, height, bufs) {
    //TODO: This is just a PoC, for a robust implementation we will
    //need to read chunks of data from the target, in e.g. tiles
    //so that we optimize the number of reads (done on mouse move)
    //compared to amount of data transfer and duplicate memory usage

    function copyBuffers(dst, src, width, height, srcStride) {

      const buf1 = dst[0];
      const buf2 = dst[1];
      const targetDataOffset = srcStride * height;

      for (let j = 0; j < height; j++) {

        let hOffSrc = srcStride * j;
        let hOffSrc2 = hOffSrc + targetDataOffset;
        let hOffDst = width * j;

        for (let i = 0; i < width; i++) {
          buf1[hOffDst] = src[hOffSrc++];
          buf2[hOffDst] = src[hOffSrc2++];
          hOffDst++;
        }
      }
    }


    if (this.#readBuffer?.mapState === 'pending') {
      return false;
    }

    let bytesPerRow = width * 4;
    let remainder = bytesPerRow % 256;
    if (remainder > 0) {
      bytesPerRow += 256 - remainder;
    }

    let bufferSize = bytesPerRow * height;
    if (!this.#readBuffer || this.#readBuffer.size < bufferSize * 2) {
      this.#readBuffer?.destroy();

      this.#readBuffer = this.#device.createBuffer({
        label: 'id-target-read-buffer',
        size: bufferSize * 2,
        usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
      });
    }

    let commandEncoder = this.#device.createCommandEncoder();

    commandEncoder.copyTextureToBuffer(
      { texture: this.getIdTargetForSampling(0), origin: [x, y] },
      { buffer: this.#readBuffer, offset: 0, bytesPerRow: bytesPerRow },
      [width, height]
    );

    commandEncoder.copyTextureToBuffer(
      { texture: this.getIdTargetForSampling(1), origin: [x, y] },
      { buffer: this.#readBuffer, offset: bufferSize, bytesPerRow: bytesPerRow },
      [width, height]
    );

    this.#device.queue.submit([commandEncoder.finish()]);

    return this.#readBuffer.mapAsync(GPUMapMode.READ, 0).then(() => {
      let range = new Uint8Array(this.#readBuffer.getMappedRange());
      copyBuffers(bufs, range, width * 4, height, bytesPerRow);

      //let test = new Int32Array(bufs[0].buffer, bufs[0].byteOffset);
      //let dbId = test[0];
      //console.log(dbId);
      this.#readBuffer.unmap();
      return true;
    });
  }

  async #cacheWholeIdTargets() {

    this.#cacheInProgress = true;

    let [width, height] = this.#size;

    let bytesPerRow = width * 4;
    let remainder = bytesPerRow % 256;
    if (remainder > 0) {
      bytesPerRow += 256 - remainder;
    }

    let bufferSize = bytesPerRow * height;
    if (!this.#readBuffer || this.#readBuffer.size < bufferSize * 2) {
      this.#readBuffer?.destroy();

      this.#readBuffer = this.#device.createBuffer({
        label: 'id-target-cache-buffer',
        size: bufferSize * 2, //one for dbIds and one for modelIds buffers
        usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
      });
    }

    let commandEncoder = this.#device.createCommandEncoder();

    commandEncoder.copyTextureToBuffer(
      { texture: this.getIdTargetForSampling(0), origin: [0, 0] },
      { buffer: this.#readBuffer, offset: 0, bytesPerRow: bytesPerRow },
      [width, height]
    );

    commandEncoder.copyTextureToBuffer(
      { texture: this.getIdTargetForSampling(1), origin: [0, 0] },
      { buffer: this.#readBuffer, offset: bufferSize, bytesPerRow: bytesPerRow },
      [width, height]
    );

    this.#device.queue.submit([commandEncoder.finish()]);

    return this.#readBuffer.mapAsync(GPUMapMode.READ, 0).then(async () => {

      // If buffer was resized during async readback, the result is worthless.
      const wasResized = width !== this.#size[0] || height !== this.#size[1];
      if (wasResized) {
        this.#readBuffer.unmap();

        // Set in-progress back to false, but don't clear the dirty flag
        this.#cacheInProgress = false;

        // Indicate that readback was not successful. In theory, we could auto-restart
        // here, but we don't want to introduce a risk of infinite loop or hidden waste (e.g. on resize-animations).
        // Note that this callback is currently null and only used for tests anyway.
        if (this.onIdReadbackCacheFinished) {
          this.onIdReadbackCacheFinished(false);
        }

        return;
      }

      let range = new Uint8Array(this.#readBuffer.getMappedRange());

      if (!this.#cachedWholeBuffers) {
        this.#cachedWholeBuffers = new Uint8Array(range.byteLength);
      }

      this.#cachedWholeBuffers.set(range);

      this.#readBuffer.unmap();

      this.#cachedIdBuffersDirty = false;
      this.#cacheInProgress = false;

      // Notify sync readback works until next invalidation
      if (this.onIdReadbackCacheFinished) {
        this.onIdReadbackCacheFinished(true);
      }
    });
  }

  //Hacky implementation of reading from a render target that is meant to work
  //with the synchronous idAtPixel() API that is used for mouse rollover.
  //Initial attempt will trigger an operation to read the entire ID buffer
  //to CPU memory, and subsequent attempts will return result immediately
  //Ideally we will transition all code that needs to read back buffers to use async/Promise
  //way
  /**
   * @param {number} x, y, width, height - region to read
   * @param {Uint8Array} [bufs] - 2 Buffers [objectIdBuf, modelIdBuf] read into.
   * 								May be omitted for a "dry run" to only check if cache is available without reading.
   * @returns {boolean} - true if cache data was available.
   */
  readIdTargetPixelsSyncOrFail(x, y, width, height, bufs) {

    const copyBuffers = (dst, src, width, height, srcStride, srcX, srcY) => {

      const buf1 = dst[0];
      const buf2 = dst[1];
      const targetDataOffset = srcStride * this.#size[1];
      const startX = srcX || 0;
      const startY = srcY || 0;

      for (let j = 0; j < height; j++) {

        let hOffSrc = srcStride * (j + startY) + startX * 4;
        let hOffSrc2 = hOffSrc + targetDataOffset;
        let hOffDst = width * j;

        for (let i = 0; i < width; i++) {
          buf1[hOffDst] = src[hOffSrc++];
          buf2[hOffDst] = src[hOffSrc2++];
          hOffDst++;
        }
      }
    };

    function setZero() {
      if (!bufs) {
        return;
      }
      for (let i = 0; i < bufs.length; i++) {
        bufs[i].fill(0);
      }
    }

    if (this.#readBuffer?.mapState === 'pending') {
      setZero();
      return false;
    }

    if (this.#cacheInProgress) {
      setZero();
      return false;
    }

    if (this.#cachedIdBuffersDirty) {
      setZero();
      this.#cacheWholeIdTargets();
      return false;
    }

    // After a resize, the cache is wiped but dirty stays false because nothing has rendered yet. Avoid reading
    // from null - the next render will call setIdTargetsDirty() and a proper re-cache will follow.
    if (!this.#cachedWholeBuffers) {
      setZero();
      return false;
    }

    let bytesPerRow = this.#size[0] * 4;
    let remainder = bytesPerRow % 256;
    if (remainder > 0) {
      bytesPerRow += 256 - remainder;
    }

    bufs && copyBuffers(bufs, this.#cachedWholeBuffers, width * 4, height, bytesPerRow, x, y);

    // Note that we also return true if bufs are not provided, because the return value
    // is only used to indicate whether the cache was available.
    return true;
  }

  setIdTargetsDirty() {
    this.#cachedIdBuffersDirty = true;
  }
}